import React, { CSSProperties, ReactElement, ReactNode, useMemo } from "react";
import {
  UIButton,
  UICheckbox,
  UIIconButton,
  UIInput,
  UIMenuItem,
  UIModal,
  UIRadio,
  UISelect,
  UISpin,
  UITextArea,
} from "../index";

type AnyProps = Record<string, any>;

type SelectChangeEvent<T = unknown> = {
  target: {
    value: T;
    checked?: boolean;
    name?: string;
  };
  preventDefault?: () => void;
};

type TextFieldChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

const palette = {
  primary: "#1677ff",
  grey200: "#e5e7eb",
  grey300: "#d1d5db",
  backgroundPaper: "#ffffff",
  textPrimary: "#111827",
  error: "#dc2626",
};

const tokenToColor = (value: unknown) => {
  if (typeof value !== "string") return value;
  const map: Record<string, string> = {
    "primary.main": palette.primary,
    "grey.200": palette.grey200,
    "grey.300": palette.grey300,
    "background.paper": palette.backgroundPaper,
    "text.primary": palette.textPrimary,
    "error.main": palette.error,
    "warning.main": "#d97706",
    "success.main": "#16a34a",
    "rgb(110, 209, 243)": "rgb(110, 209, 243)",
  };
  return map[value] ?? value;
};

const unit = (value: any) => {
  if (typeof value === "number") return `${value * 8}px`;
  return value;
};

const sxToStyle = (sx: any): CSSProperties => {
  if (!sx) return {};
  if (Array.isArray(sx)) {
    return sx.reduce((acc, item) => ({ ...acc, ...sxToStyle(item) }), {} as CSSProperties);
  }
  if (typeof sx !== "object") return {};

  const style: CSSProperties = {};
  Object.entries(sx).forEach(([key, value]) => {
    if (value == null || typeof value === "object") return;

    const cssKey = key
      .replace(/-([a-z])/g, (_, c) => c.toUpperCase())
      .replace(/^bgcolor$/, "backgroundColor");

    const mappedValue =
      key === "m" ||
      key === "mt" ||
      key === "mr" ||
      key === "mb" ||
      key === "ml" ||
      key === "mx" ||
      key === "my" ||
      key === "p" ||
      key === "pt" ||
      key === "pr" ||
      key === "pb" ||
      key === "pl" ||
      key === "px" ||
      key === "py"
        ? unit(value)
        : tokenToColor(value);

    if (key === "m") {
      style.margin = mappedValue as any;
      return;
    }
    if (key === "mt") {
      style.marginTop = mappedValue as any;
      return;
    }
    if (key === "mr") {
      style.marginRight = mappedValue as any;
      return;
    }
    if (key === "mb") {
      style.marginBottom = mappedValue as any;
      return;
    }
    if (key === "ml") {
      style.marginLeft = mappedValue as any;
      return;
    }
    if (key === "mx") {
      style.marginLeft = mappedValue as any;
      style.marginRight = mappedValue as any;
      return;
    }
    if (key === "my") {
      style.marginTop = mappedValue as any;
      style.marginBottom = mappedValue as any;
      return;
    }
    if (key === "p") {
      style.padding = mappedValue as any;
      return;
    }
    if (key === "pt") {
      style.paddingTop = mappedValue as any;
      return;
    }
    if (key === "pr") {
      style.paddingRight = mappedValue as any;
      return;
    }
    if (key === "pb") {
      style.paddingBottom = mappedValue as any;
      return;
    }
    if (key === "pl") {
      style.paddingLeft = mappedValue as any;
      return;
    }
    if (key === "px") {
      style.paddingLeft = mappedValue as any;
      style.paddingRight = mappedValue as any;
      return;
    }
    if (key === "py") {
      style.paddingTop = mappedValue as any;
      style.paddingBottom = mappedValue as any;
      return;
    }

    (style as any)[cssKey] = mappedValue;
  });

  return style;
};

const mergeStyle = (style?: CSSProperties, sx?: any, system?: CSSProperties): CSSProperties => ({
  ...sxToStyle(sx),
  ...(system ?? {}),
  ...(style ?? {}),
});

const asStyleFromProps = (props: AnyProps): { styleProps: CSSProperties; rest: AnyProps } => {
  const {
    display,
    alignItems,
    justifyContent,
    flexDirection,
    flexWrap,
    flexGrow,
    gap,
    width,
    height,
    minHeight,
    minWidth,
    maxHeight,
    maxWidth,
    position,
    top,
    right,
    bottom,
    left,
    zIndex,
    overflow,
    overflowY,
    textAlign,
    bgcolor,
    color,
    borderRadius,
    boxShadow,
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    transform,
    ...rest
  } = props;

  const styleProps: CSSProperties = {
    display,
    alignItems,
    justifyContent,
    flexDirection,
    flexWrap,
    flexGrow,
    gap,
    width,
    height,
    minHeight,
    minWidth,
    maxHeight,
    maxWidth,
    position,
    top,
    right,
    bottom,
    left,
    zIndex,
    overflow,
    overflowY,
    textAlign,
    backgroundColor: tokenToColor(bgcolor) as string,
    color: tokenToColor(color) as string,
    borderRadius,
    boxShadow,
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    transform,
  };

  return { styleProps, rest };
};

export const Box = ({ component = "div", sx, style, children, ...props }: AnyProps) => {
  const { styleProps, rest } = asStyleFromProps(props);
  const mergedStyle = mergeStyle(style, sx, styleProps);
  return React.createElement(component, { ...rest, style: mergedStyle }, children);
};

export const Typography = ({
  component,
  variant = "body1",
  sx,
  style,
  children,
  ...props
}: AnyProps) => {
  const variantStyle: Record<string, CSSProperties> = {
    h6: { fontSize: 20, fontWeight: 600 },
    subtitle1: { fontSize: 16, fontWeight: 600 },
    body1: { fontSize: 16 },
    body2: { fontSize: 14 },
    caption: { fontSize: 12 },
  };

  const tag = component ?? (variant.startsWith("h") ? variant : "div");
  return React.createElement(
    tag,
    { ...props, style: mergeStyle(style, sx, variantStyle[variant] ?? {}) },
    children
  );
};

export const Button = ({
  component,
  startIcon,
  endIcon,
  children,
  variant,
  color,
  type,
  sx,
  style,
  onClick,
  ...props
}: AnyProps & { onClick?: React.MouseEventHandler<HTMLElement> }) => {
  const content = (
    <>
      {startIcon ? <span style={{ marginRight: 6, display: "inline-flex" }}>{startIcon}</span> : null}
      {children}
      {endIcon ? <span style={{ marginLeft: 6, display: "inline-flex" }}>{endIcon}</span> : null}
    </>
  );

  if (component === "label") {
    return (
      <label style={mergeStyle(style, sx)} {...props}>
        {content}
      </label>
    );
  }

  if (component && component !== "button") {
    return React.createElement(component, { ...props, style: mergeStyle(style, sx) }, content);
  }

  return (
    <UIButton
      type={type}
      variant={variant === "outlined" ? "outlined" : variant === "text" ? "text" : "contained"}
      buttonType={color === "primary" || variant === "contained" ? "primary" : "default"}
      sx={mergeStyle(style, sx) as any}
      onClick={onClick as any}
      {...props}
    >
      {content}
    </UIButton>
  );
};

export const IconButton = ({
  component,
  children,
  sx,
  style,
  onClick,
  ...props
}: AnyProps & { onClick?: React.MouseEventHandler<HTMLElement> }) => {
  if (component && component !== "button") {
    return React.createElement(component, { ...props, style: mergeStyle(style, sx) }, children);
  }

  return (
    <UIIconButton sx={mergeStyle(style, sx) as any} onClick={onClick as any} {...props}>
      {children}
    </UIIconButton>
  );
};

export const Checkbox = ({ sx, style, onChange, ...props }: AnyProps) => {
  return (
    <UICheckbox
      sx={mergeStyle(style, sx) as any}
      onChange={(event) =>
        onChange?.({
          ...event,
          target: {
            ...(event?.target ?? {}),
            checked: event?.target?.checked,
            name: (props as AnyProps)?.name,
            value: (props as AnyProps)?.value,
          },
        })
      }
      {...props}
    />
  );
};

export const Radio = ({ sx, style, ...props }: AnyProps) => (
  <UIRadio style={mergeStyle(style, sx)} {...props} />
);

export const RadioGroup = ({ children, value, name, onChange, ...props }: AnyProps) => (
  <div {...props}>
    {React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
      return React.cloneElement(child as ReactElement<any>, {
        radioGroupValue: value,
        radioGroupName: name,
        radioGroupOnChange: onChange,
      });
    })}
  </div>
);

export const FormControl = ({ component = "div", children, ...props }: AnyProps) =>
  React.createElement(component, props, children);

export const FormLabel = ({ component = "label", children, ...props }: AnyProps) =>
  React.createElement(component, props, children);

export const FormGroup = ({ children, ...props }: AnyProps) => <div {...props}>{children}</div>;

export const FormControlLabel = ({
  control,
  label,
  value,
  disabled,
  radioGroupValue,
  radioGroupName,
  radioGroupOnChange,
  ...props
}: AnyProps) => {
  let controlElement = control;

  if (React.isValidElement(control)) {
    const existingOnChange = (control.props as AnyProps).onChange;

    controlElement = React.cloneElement(control as ReactElement<any>, {
      value,
      name: radioGroupName ?? (control.props as AnyProps).name,
      disabled: disabled ?? (control.props as AnyProps).disabled,
      checked:
        radioGroupValue !== undefined
          ? String(radioGroupValue) === String(value)
          : (control.props as AnyProps).checked,
      onChange: (event: any) => {
        if (radioGroupOnChange) {
          radioGroupOnChange({
            ...event,
            target: {
              ...(event?.target ?? {}),
              value,
              checked: event?.target?.checked,
              name: radioGroupName,
            },
          });
        }
        if (existingOnChange) {
          existingOnChange(event);
        }
      },
    });
  }

  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, opacity: disabled ? 0.6 : 1 }} {...props}>
      {controlElement}
      <span>{label}</span>
    </label>
  );
};

export const InputAdornment = ({ children, ...props }: AnyProps) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} {...props}>
    {children}
  </span>
);

export const InputLabel = ({ children, ...props }: AnyProps) => <label {...props}>{children}</label>;

export const Select = ({
  children,
  onChange,
  value,
  fullWidth,
  sx,
  style,
  ...props
}: AnyProps & {
  onChange?: (event: SelectChangeEvent<any>, child?: ReactNode) => void;
}) => {
  return (
    <UISelect<any>
      value={value}
      onChange={(event) => {
        onChange?.({
          ...event,
          target: {
            ...(event?.target ?? {}),
            value: event?.target?.value,
          },
        });
      }}
      style={mergeStyle(style, sx, fullWidth ? { width: "100%" } : {})}
      {...props}
    >
      {children}
    </UISelect>
  );
};

export const MenuItem = ({ children, value, ...props }: AnyProps) => (
  <UIMenuItem value={value} {...props}>
    {children}
  </UIMenuItem>
);

export const TextField = React.forwardRef<
  any,
  AnyProps & {
    onChange?: (event: TextFieldChangeEvent) => void;
    onBlur?: (event: TextFieldChangeEvent) => void;
  }
>((props, ref) => {
  const {
    className,
    select,
    label,
    value,
    defaultValue,
    onChange,
    onBlur,
    name,
    id,
    type = "text",
    placeholder,
    disabled,
    fullWidth,
    margin,
    multiline,
    rows = 3,
    helperText,
    error,
    inputProps,
    InputProps,
    sx,
    style,
    children,
    ...rest
  } = props;

  const wrapperStyle = mergeStyle(style, sx, {
    width: fullWidth ? "100%" : undefined,
    marginBottom: margin === "normal" ? 16 : undefined,
  });

  const content = useMemo(() => {
    if (select) {
      return (
        <Select
          id={id}
          value={value}
          onChange={(event: SelectChangeEvent<any>) => onChange?.(event as any)}
          fullWidth={fullWidth}
          disabled={disabled}
          name={name}
          {...rest}
        >
          {children}
        </Select>
      );
    }

    if (multiline) {
      return (
        <UITextArea
          ref={ref}
          id={id}
          name={name}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          rows={rows}
          placeholder={placeholder}
          fullWidth={fullWidth}
          {...inputProps}
          {...rest}
        />
      );
    }

    return (
      <UIInput
        noWrapper
        ref={ref}
        id={id}
        name={name}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={placeholder}
        type={type}
        style={{
          width: "100%",
          height: 32,
          border: "1px solid #d9d9d9",
          borderRadius: 6,
          padding: "0 8px",
        }}
        {...inputProps}
        {...rest}
      />
    );
  }, [
    children,
    defaultValue,
    disabled,
    fullWidth,
    id,
    inputProps,
    multiline,
    name,
    onBlur,
    onChange,
    placeholder,
    ref,
    rest,
    rows,
    select,
    type,
    value,
  ]);

  return (
    <div className={className} style={wrapperStyle}>
      {label ? <label style={{ display: "block", marginBottom: 6 }}>{label}</label> : null}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {InputProps?.startAdornment ?? null}
        <div style={{ flex: 1 }}>{content}</div>
        {InputProps?.endAdornment ?? null}
      </div>
      {helperText ? (
        <div style={{ marginTop: 4, fontSize: 12, color: error ? palette.error : "#6b7280" }}>{helperText}</div>
      ) : null}
    </div>
  );
});

TextField.displayName = "MuiCompatTextField";

export const Tooltip = ({ title, children }: AnyProps) => (
  <span title={typeof title === "string" ? title : undefined}>{children}</span>
);

export const Link = ({ children, ...props }: AnyProps) => <a {...props}>{children}</a>;

export const Modal = ({ open, onClose, children, ...props }: AnyProps) => {
  if (!open) return null;

  return (
    <UIModal open={open} onClose={onClose} {...props}>
      {children}
    </UIModal>
  );
};

export const Dialog = Modal;

export const DialogTitle = ({ children, ...props }: AnyProps) => (
  <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }} {...props}>
    {children}
  </div>
);

export const DialogContent = ({ children, ...props }: AnyProps) => <div {...props}>{children}</div>;

export const DialogContentText = ({ children, ...props }: AnyProps) => (
  <div style={{ color: "#6b7280", marginBottom: 8 }} {...props}>
    {children}
  </div>
);

export const DialogActions = ({ children, ...props }: AnyProps) => (
  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }} {...props}>
    {children}
  </div>
);

const getAnchorPosition = (anchorEl: HTMLElement | null) => {
  if (!anchorEl) return { top: 120, left: 120 };
  const rect = anchorEl.getBoundingClientRect();
  return {
    top: rect.bottom + 8,
    left: rect.left,
  };
};

const FloatingLayer = ({ open, anchorEl, onClose, children, sx, style }: AnyProps) => {
  if (!open) return null;
  const pos = getAnchorPosition(anchorEl ?? null);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
      }}
      onClick={() => onClose?.()}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={mergeStyle(style, sx, {
          position: "fixed",
          top: pos.top,
          left: pos.left,
          backgroundColor: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
        })}
      >
        {children}
      </div>
    </div>
  );
};

export const Popover = (props: AnyProps) => <FloatingLayer {...props} />;

export const Popper = (props: AnyProps) => <FloatingLayer {...props} />;

export const CircularProgress = ({ size = "small" }: AnyProps) => <UISpin size={size} />;

export const Divider = ({ ...props }: AnyProps) => <hr style={{ borderColor: "#e5e7eb", margin: "8px 0" }} {...props} />;

export const Grid = ({ container, item, spacing = 0, xs, sx, style, children, ...props }: AnyProps) => {
  const gridStyle: CSSProperties = {};

  if (container) {
    gridStyle.display = "flex";
    gridStyle.flexWrap = "wrap";
    gridStyle.gap = typeof spacing === "number" ? spacing * 8 : spacing;
  }

  if (item && typeof xs === "number") {
    const width = `${(xs / 12) * 100}%`;
    gridStyle.flex = `0 0 ${width}`;
    gridStyle.maxWidth = width;
  }

  return (
    <div style={mergeStyle(style, sx, gridStyle)} {...props}>
      {children}
    </div>
  );
};

export const Stack = ({ direction = "column", spacing = 0, sx, style, children, ...props }: AnyProps) => (
  <div
    style={mergeStyle(style, sx, {
      display: "flex",
      flexDirection: direction,
      gap: typeof spacing === "number" ? spacing * 8 : spacing,
    })}
    {...props}
  >
    {children}
  </div>
);

export const Container = ({ maxWidth = "md", sx, style, children, ...props }: AnyProps) => {
  const map: Record<string, number> = {
    xs: 444,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  };
  const max = typeof maxWidth === "string" ? map[maxWidth] : undefined;
  return (
    <div
      style={mergeStyle(style, sx, {
        width: "100%",
        maxWidth: max,
        marginLeft: "auto",
        marginRight: "auto",
      })}
      {...props}
    >
      {children}
    </div>
  );
};

export const Card = ({ children, sx, style, ...props }: AnyProps) => (
  <div
    style={mergeStyle(style, sx, {
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      backgroundColor: "#fff",
    })}
    {...props}
  >
    {children}
  </div>
);

export const CardContent = ({ children, sx, style, ...props }: AnyProps) => (
  <div style={mergeStyle(style, sx, { padding: 16 })} {...props}>
    {children}
  </div>
);

export const NativeSelect = ({ children, sx, style, ...props }: AnyProps) => (
  <select style={mergeStyle(style, sx)} {...props}>
    {children}
  </select>
);

export const Alert = ({ severity = "info", children, sx, style, ...props }: AnyProps) => {
  const colors: Record<string, string> = {
    info: "#dbeafe",
    success: "#dcfce7",
    warning: "#fef3c7",
    error: "#fee2e2",
  };
  return (
    <div
      style={mergeStyle(style, sx, {
        backgroundColor: colors[severity] ?? colors.info,
        borderRadius: 8,
        padding: 12,
      })}
      {...props}
    >
      {children}
    </div>
  );
};

export const AlertTitle = ({ children, ...props }: AnyProps) => (
  <div style={{ fontWeight: 600, marginBottom: 4 }} {...props}>
    {children}
  </div>
);

export const Grow = ({ in: inProp = true, children }: AnyProps) => (inProp ? <>{children}</> : null);

export const List = ({ children, ...props }: AnyProps) => <ul style={{ listStyle: "none", margin: 0, padding: 0 }} {...props}>{children}</ul>;

export const ListItem = ({ children, ...props }: AnyProps) => <li {...props}>{children}</li>;

export const ListItemText = ({ primary, secondary, ...props }: AnyProps) => (
  <div {...props}>
    {primary}
    {secondary ? <div style={{ opacity: 0.7 }}>{secondary}</div> : null}
  </div>
);

export type { SelectChangeEvent };
